import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  Fab,
  CircularProgress,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import AudioPlayer from "./AudioPlayer";
import TextGenerateEffect from "./TextGenerateEffect";

const WidgetThinkingAnimation = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          "& .dot": {
            width: 5,
            height: 5,
            backgroundColor: "primary.main",
            borderRadius: "50%",
            animation: "bounce 1.4s infinite ease-in-out both",
            "&:nth-of-type(1)": {
              animationDelay: "-0.32s",
            },
            "&:nth-of-type(2)": {
              animationDelay: "-0.16s",
            },
          },
          "@keyframes bounce": {
            "0%, 80%, 100%": {
              transform: "scale(0)",
            },
            "40%": {
              transform: "scale(1)",
            },
          },
        }}
      >
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </Box>
    </Box>
  );
};

const AIChatWidget = ({ userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [animatingMessageIndex, setAnimatingMessageIndex] = useState(null);
  const [completedAnimations, setCompletedAnimations] = useState(new Set());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (message) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          question: message,
        }),
      });
      console.log(userId, message);
      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      console.log("Raw API response:", data);

      const aiResponse =
        typeof data.message === "string"
          ? data.message
          : JSON.stringify(data.message);

      const newMessageIndex = messages.length + 1;
      setMessages((prev) => [
        ...prev,
        { role: "user", content: message },
        { role: "assistant", content: aiResponse },
      ]);
      setAnimatingMessageIndex(newMessageIndex);

      setInput("");
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: message },
        {
          role: "assistant",
          content: `Error: ${error.message}. Please try again.`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (!isOpen) {
    return (
      <Fab
        color="primary"
        aria-label="chat"
        style={{ position: "fixed", bottom: 20, right: 20 }}
        onClick={() => setIsOpen(true)}
      >
        <ChatIcon />
      </Fab>
    );
  }

  return (
    <Paper
      elevation={3}
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 350,
        height: 500,
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          p: 2,
          backgroundColor: "primary.main",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Chat with CoachCAROL</Typography>
        <IconButton color="inherit" onClick={() => setIsOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              textAlign: message.role === "user" ? "right" : "left",
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor:
                message.role === "user" ? "primary.light" : "background.paper",
            }}
          >
            {message.role === "user" ? (
              <Typography variant="body1">{message.content}</Typography>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {!completedAnimations.has(index) &&
                index === animatingMessageIndex ? (
                  <TextGenerateEffect
                    text={message.content}
                    onComplete={() => {
                      setCompletedAnimations(
                        (prev) => new Set([...prev, index])
                      );
                      setAnimatingMessageIndex(null);
                    }}
                  />
                ) : (
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                )}
                <AudioPlayer text={message.content} />
              </Box>
            )}
          </Box>
        ))}
        {isLoading && <WidgetThinkingAnimation />}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ p: 2, backgroundColor: "background.paper" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => sendMessage(input)}
                disabled={isLoading || !input.trim()}
                sx={{
                  color: "primary.main",
                  "&.Mui-disabled": {
                    opacity: 0.7,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            ),
          }}
        />
      </Box>
    </Paper>
  );
};

export default AIChatWidget;
